import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const SLIDES = [
  {
    id: "apparel",
    tag: "MOST POPULAR",
    headline: "CUSTOM APPAREL",
    sub: "SCREEN PRINTING // DTF // EMBROIDERY",
    desc: "T-shirts, hoodies, polos & more. From 12 to 10,000+ pieces. Upload your art — get an instant quote.",
    cta: "START YOUR ORDER",
    ctaLink: "/design-studio",
    ctaSec: "VIEW PRODUCTS",
    ctaSecLink: "/products",
    img: "https://customer-assets.emergentagent.com/job_d4287510-3032-43dc-a2e9-61b11f4b1517/artifacts/g4u37nhh_fpb%202.png",
    accent: "signal",
  },
  {
    id: "stickers",
    tag: "NEW",
    headline: "CUSTOM STICKERS",
    sub: "KISS CUT // DIE CUT // ANY SHAPE",
    desc: "Full-color, waterproof vinyl stickers from $0.15/ea. 500+ pieces = 50% off. Perfect for branding.",
    cta: "DESIGN STICKERS",
    ctaLink: "/design-studio",
    ctaSec: "VIEW PRICING",
    ctaSecLink: "/products/stickers",
    img: "https://images.unsplash.com/photo-1600823385750-49445b66ab9e?w=720&h=480&fit=crop&q=80",
    accent: "acid",
  },
  {
    id: "signs",
    tag: "B2B",
    headline: "SIGNS & BANNERS",
    sub: "VINYL BANNERS // YARD SIGNS // CANOPIES",
    desc: "Large format printing for events, retail, and advertising. Weather-resistant, vibrant full-color prints.",
    cta: "GET A QUOTE",
    ctaLink: "/concierge-quote",
    ctaSec: "BROWSE PRODUCTS",
    ctaSecLink: "/products",
    img: "https://customer-assets.emergentagent.com/job_d4287510-3032-43dc-a2e9-61b11f4b1517/artifacts/oqxr1jxb_fpb%206.png",
    accent: "cobalt",
  },
];

const ACCENT_COLORS = {
  signal: { bg: "bg-signal", text: "text-bone", offset: "#FF2D1A" },
  acid: { bg: "bg-acid", text: "text-ink", offset: "#D4FF3D" },
  cobalt: { bg: "bg-cobalt", text: "text-bone", offset: "#1635E8" },
};

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent(c => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [paused, next]);

  const slide = SLIDES[current];
  const accent = ACCENT_COLORS[slide.accent] || ACCENT_COLORS.signal;

  return (
    <section className="border-b-2 border-ink" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-section">
      {/* Main hero band */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left: Typography */}
          <div>
            {/* Tag */}
            <div className="flex items-center gap-3 mb-4">
              <span className={`inline-block ${accent.bg} ${accent.text} font-mono text-[10px] uppercase tracking-[0.14em] font-bold px-3 py-1 border-2 border-ink`} data-testid="hero-tag">
                {slide.tag}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
                {String(current + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-archivo text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] uppercase leading-[0.92] tracking-tight text-ink mb-4" data-testid="hero-headline">
              {slide.headline}
            </h1>

            {/* Sub */}
            <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted mb-4" data-testid="hero-sub">
              {slide.sub}
            </p>

            {/* Description */}
            <p className="font-inter text-base text-ink-soft leading-relaxed max-w-md mb-6">
              {slide.desc}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Link
                to={slide.ctaLink}
                className={`inline-flex items-center gap-2 ${accent.bg} ${accent.text} border-2 border-ink font-inter text-sm uppercase font-bold tracking-wider px-6 py-3 transition-all`}
                style={{ boxShadow: `6px 6px 0px 0px #0A0A0A` }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translate(-2px,-2px)"; e.currentTarget.style.boxShadow = "8px 8px 0px 0px #0A0A0A"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "6px 6px 0px 0px #0A0A0A"; }}
                data-testid="hero-cta-primary"
              >
                {slide.cta} <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
              <Link
                to={slide.ctaSecLink}
                className="inline-flex items-center gap-2 bg-paper text-ink border-2 border-ink font-inter text-sm uppercase font-bold tracking-wider px-6 py-3 transition-all hover:bg-ink hover:text-acid"
                data-testid="hero-cta-secondary"
              >
                {slide.ctaSec}
              </Link>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative">
            <div className="border-2 border-ink bg-bone overflow-hidden" style={{ boxShadow: `6px 6px 0px 0px ${accent.offset}` }}>
              <img
                src={slide.img}
                alt={slide.headline}
                className="w-full h-[280px] sm:h-[340px] object-cover"
              />
            </div>
            {/* Slide nav */}
            <div className="flex items-center gap-0 mt-3">
              <button onClick={prev} className="p-2 border-2 border-ink bg-paper text-ink hover:bg-ink hover:text-acid transition-colors" data-testid="hero-prev">
                <ArrowLeft weight="bold" className="w-4 h-4" />
              </button>
              <button onClick={next} className="p-2 border-2 border-ink border-l-0 bg-paper text-ink hover:bg-ink hover:text-acid transition-colors" data-testid="hero-next">
                <ArrowRight weight="bold" className="w-4 h-4" />
              </button>
              <div className="flex gap-1 ml-3">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setCurrent(i)}
                    className={`w-8 h-1.5 border border-ink transition-colors ${i === current ? "bg-ink" : "bg-stock"}`}
                    data-testid={`hero-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="border-t-2 border-ink bg-acid overflow-hidden py-2.5">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, copy) => (
            <div key={`marquee-${copy}`} className="flex items-center shrink-0">
              {["SCREEN PRINTING", "DTF TRANSFERS", "EMBROIDERY", "CUSTOM STICKERS", "VINYL BANNERS", "YARD SIGNS", "EVENT PACKAGES", "AI ARTWORK ENHANCEMENT", "FREE DIGITIZING"].map((item) => (
                <span key={`${copy}-${item}`} className="font-archivo text-lg uppercase tracking-tight text-ink px-6">
                  {item} <span className="text-signal mx-3">//</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { Hero };
export default Hero;
